import { getDb } from "@/services/storage/sqlite/connection";
import { createId } from "@/utils";
import type { ConversationFolder } from "@/types";

interface ConversationFolderRow {
  id: string;
  workspace_id: string;
  name: string;
  created_at: number;
}

function rowToFolder(row: ConversationFolderRow): ConversationFolder {
  return { id: row.id, workspaceId: row.workspace_id, name: row.name, createdAt: row.created_at };
}

/** Folder CRUD for conversation organization — kept separate from
 * `sqliteConversationRepository.ts` so that file stays focused on its
 * existing `ConversationRepository` contract (list/get/save/delete of a
 * whole conversation record). Pin/favorite/branch/duplicate all mutate
 * fields already on `Conversation`/`ChatMessage`, so they go through the
 * normal `save()` full-record upsert instead of a bespoke method here. */
export const conversationOrganizationRepository = {
  async listFolders(workspaceId: string): Promise<ConversationFolder[]> {
    const db = await getDb();
    const rows = await db.select<ConversationFolderRow[]>(
      "SELECT * FROM conversation_folders WHERE workspace_id = $1 ORDER BY name ASC",
      [workspaceId],
    );
    return rows.map(rowToFolder);
  },

  async createFolder(workspaceId: string, name: string): Promise<ConversationFolder> {
    const db = await getDb();
    const folder: ConversationFolder = { id: createId("cfld"), workspaceId, name, createdAt: Date.now() };
    await db.execute(
      "INSERT INTO conversation_folders (id, workspace_id, name, created_at) VALUES ($1, $2, $3, $4)",
      [folder.id, folder.workspaceId, folder.name, folder.createdAt],
    );
    return folder;
  },

  async deleteFolder(id: string): Promise<void> {
    const db = await getDb();
    await db.execute("DELETE FROM conversation_folders WHERE id = $1", [id]);
  },
};
